import Link from "next/link";
import { EXCLUSIVE_ROUTE, LUXE_INTERNAL_ROUTE } from "@/lib/luxe-path.mjs";

/**
 * Menulink naar /exclusive (het publieke adres sinds 05-10-2026) met het gouden ruitje (ontwerp v2, lane LP10). Favotrip
 * Exclusive (04-10-2026): desktop "Exclusive" (volle naam als titel; de rij op
 * 1280 px heeft ~32 px over, de volle naam is ~88 px breder), mobiel de volle naam.
 * "Exclusive" is ~30 px breder dan "Luxe": op 1280 px blijft dan ~2 px over, en
 * met een vaste schuifbalk (Windows, ~17 px) breekt de rij tussen 1280 en ~1296 px.
 * Daarom staat het ruitje op desktop pas vanaf 1320 px (de containerbreedte):
 * dat geeft ~22 px terug, en vanaf 1320 px is er ~42 px over. Lane LP14
 * voegt de desktop-icoonvariant van "Zoek aanbieding" toe; lane LP14b zet
 * hem aan (zie LUXE_NAV_LINK_LIVE).
 * De Navbar toont hem alleen op favotrip.nl (markt nl-nl), direct na
 * "Aanbiedingen" zoals in v2: Luxe bestaat alleen daar, want alleen daar ziet
 * de server de klantsessie. Ook niet-ingelogden zien de link; /luxe toont hen
 * het poortscherm.
 *
 * Statisch: geen sessiecheck en geen dealdata. De Navbar is een
 * clientcomponent op elke pagina, dus de link verraadt niets.
 *
 * De Luxe-look (het ruitje) staat hier, niet in de Navbar, zodat de gedeelde
 * Navbar alleen twee conditionele regels krijgt.
 */
export const LUXE_NAV_HREF = EXCLUSIVE_ROUTE;

/**
 * Aan/uit-knop voor de menulink (desktop en mobiel samen) EN de bijbehorende
 * desktopwijziging in de Navbar. Op `false` rendert de Navbar exact zoals
 * vóór Luxe; /luxe zelf blijft bestaan en bereikbaar via de directe URL.
 *
 * Waarom de knop meer doet dan de link: met de link erbij past de desktoprij
 * niet (tekort 53 px op 1280, 13 px vanaf 1320; de taalknop brak dan af naar
 * twee regels en de header werd zo'n 12 px hoger). Dennis koos op 30-09
 * (optie a uit PR #1559): "Zoek aanbieding" op desktop alleen als icoon. Die
 * icoonvariant hangt aan dezelfde voorwaarde als de link (nl-nl en deze
 * knop), zodat één regel de hele wijziging aan- en uitzet. Metingen: PR #1559
 * (vóór) en de LP14-PR (na).
 *
 * Voorwaarde voor "aan": het poortscherm belooft "Handgekozen hotels
 * wereldwijd, tegen ledenprijzen" en "alleen voor leden" (LuxeGate.js), en via
 * het menu ziet elke nl-nl-bezoeker dat. Sinds Favotrip Exclusive (04-10-2026,
 * ook 4-sterrenhotels) belooft het poortscherm geen sterren meer; de
 * Luxe-publicatiepoort in site-backend (site-backend#3915, LP15) blijft per
 * hotel bepalen wat live mag. De regiolijst is uitgebreid naar de wereldlanden
 * in site-backend#3978 (LL1a). De regio-eis wordt door de backend afgedwongen;
 * de frontend heeft geen eigen regiofilter. "Alleen voor leden" dwingt de
 * Luxe-ledenpoort af (o.a. site-backend#3812). De knop ging daarom pas aan in
 * een aparte PR (LP14b), nadat die poorten gemerged en uitgerold waren.
 * Wordt de poort later verwijderd of versoepeld, zet dan ook deze knop uit.
 * Regel voor agents en reviewers: CLAUDE.md, sectie "Luxe-oppervlakken".
 *
 * Aan- of uitzetten = deze ene regel plus de test die de gecommitte stand
 * vastpint (`Navbar.luxe-link.test.jsx`, "gecommitte stand"); de overige
 * tests zetten beide standen zelf en hoeven niet mee.
 */
export const LUXE_NAV_LINK_LIVE = true;

/**
 * Actief onder /exclusive en /exclusive/... (het publieke adres, 05-10-2026)
 * en onder het oude /luxe[/...], niet onder paden die toevallig met
 * "exclusive" of "luxe" beginnen (zoals "/luxehotels"; zie ook
 * src/lib/luxe/luxe-login.js).
 * @param {string} pathname pad zonder taalprefix
 */
export function isLuxeNavActive(pathname) {
  const path = pathname || "";
  return [LUXE_NAV_HREF, LUXE_INTERNAL_ROUTE].some((root) => path === root || path.startsWith(`${root}/`));
}

function LuxeGem({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="#C8A24A" strokeWidth="1.8" aria-hidden="true">
      <path d="M12 2.5 21.5 12 12 21.5 2.5 12z" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * @param {{ variant?: "desktop" | "mobile", active?: boolean, onClick?: () => void }} props
 */
export default function LuxeNavLink({ variant = "desktop", active = false, onClick }) {
  if (variant === "mobile") {
    return (
      <Link
        href={LUXE_NAV_HREF}
        prefetch={false}
        onClick={onClick}
        data-testid="nav-luxe-link-mobile"
        aria-current={active ? "page" : undefined}
        className="flex items-center gap-2 px-4 py-4 text-base font-bold text-gray-900 hover:text-blue-600"
        style={{ minHeight: "56px" }}
      >
        <LuxeGem />
        Favotrip Exclusive
      </Link>
    );
  }
  return (
    <Link
      href={LUXE_NAV_HREF}
      prefetch={false}
      data-testid="nav-luxe-link"
      title="Favotrip Exclusive"
      aria-current={active ? "page" : undefined}
      className={`inline-flex items-center gap-1 px-1.5 py-2 whitespace-nowrap text-[16px] text-black/65 transition-colors hover:text-blue-600 ${active ? "font-semibold" : ""}`}
    >
      <LuxeGem className="hidden h-4 w-4 min-[1320px]:block" />
      Exclusive
    </Link>
  );
}
